import React from 'react';
import ContractsRow, { ContractRowProps } from './ContractsRow';
import { gql, DocumentNode } from '@apollo/client';
import { SignContractsModalContactFragment, ContractStatus } from '#types';
import MuiModal, { MuiModalProps } from '../../../mui/MuiModal/MuiModal';
import { Divider, List } from '@mui/material';
import { Button } from '../../Button';
import { notEmpty } from '../../../lib/helpers/apollo';
import { handleClose } from '../closeContractSignModalIfOpen';

// sometimes we need this on Contracts and sometimes on [Contract!]
const signContractsModalContractFragment = gql`
	fragment signContractsModalContract on SignatureContract {
		id
		status
		...signContractsModalRowContract
	}
	${ ContractsRow.fragments.signContractsModalRowContractFragment }
`;

const signContractsModalContactFragment = gql`
	fragment signContractsModalContact on Contact {
		id
		customerSignatures {
			id
			signature {
				id
				signingDate
				contract {
					...signContractsModalContract
				}
			}
		}
		vendor {
			id
			name
		}
	}
	${ signContractsModalContractFragment }
`;

const signContractsModalContactForGetContractWhereFragment = gql`
	fragment signContractsModalContactForGetContractWhere on ContactForGetContractWhere {
		id
		customerSignatures {
			id
			signature {
				id
				signingDate
				contract {
					...signContractsModalContract
				}
			}
		}
		vendor {
			id
			name
		}
	}
	${ signContractsModalContractFragment }
`;

export type SignContractsModalProps = {
	title?: string;
	contact?: SignContractsModalContactFragment | null;
	paymentText?: string;
	excludedDocumentId: string;
} & Pick<MuiModalProps['muiModalProps'], 'open' | 'onClose'> &
Pick<ContractRowProps, 'onNavigate'>;

type SignContractsModalPropsWithFragment = React.FC<SignContractsModalProps> & {
	fragments: {
		signContractsModalContactForGetContractWhereFragment: DocumentNode;
		signContractsModalContactFragment: DocumentNode;
		signContractsModalContractFragment: DocumentNode;
	};
};

const SignContractsModal: SignContractsModalPropsWithFragment = ( {
	open,
	onClose,
	contact,
	onNavigate,
	title,
	excludedDocumentId,
} ) => {
	if ( !contact?.customerSignatures ) {
		return null;
	}
	const contracts = contact.customerSignatures
		.filter(
			( customerSignature ) => customerSignature.signature.signingDate === null
		)
		.map( ( customerSignature ) => customerSignature.signature.contract )
		.filter( notEmpty )
		.filter( ( contract ) =>
			[ ContractStatus.Sent, ContractStatus.PartiallySigned ].includes(
				contract.status
			)
		);
	return (
		<MuiModal
			muiModalProps={ { open, onClose: handleClose( onClose ) } }
			title={ `${ title }! Next up...` }
			description={ `You also have these contracts from ${ contact.vendor.name } to sign.` }
			controls={
				<Button variant='outlined' onClick={ handleClose( onClose ) }>
					Close
				</Button>
			}
		>
			<List disablePadding>
				{ contracts
					.filter( ( contract ) => contract.id !== excludedDocumentId )
					.map( ( contract, index ) => (
						<React.Fragment key={ contract.id }>
							<ContractsRow
								contract={ contract }
								onNavigate={ handleClose( onNavigate ) }
							/>
							{ index < contracts.length - 1 ? <Divider /> : null }
						</React.Fragment>
					) ) }
			</List>
		</MuiModal>
	);
};

SignContractsModal.fragments = {
	signContractsModalContactFragment,
	signContractsModalContractFragment,
	signContractsModalContactForGetContractWhereFragment,
};

export default SignContractsModal;
